import { X } from 'lucide-react';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/utils';

/** Filter chip: All / Active / Expiring / No clicks. */
export interface FilterChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

export function FilterChip({ active, className, children, type = 'button', ...rest }: FilterChipProps) {
  return (
    <button
      type={type}
      aria-pressed={active}
      className={cn(
        'h-9 rounded-full border px-4 text-sm font-medium transition-colors',
        active
          ? 'border-brand-blue/60 bg-brand-blue/20 text-info'
          : 'border-border text-text-2 hover:border-border-strong hover:text-text',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/** Coloured tag chip: newsletter / pricing. */
export type TagTone = 'blue' | 'green' | 'teal' | 'neutral' | 'warning' | 'danger';

export interface TagProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: TagTone;
  onRemove?: () => void;
  size?: 'sm' | 'md';
}

const tones: Record<TagTone, string> = {
  blue: 'bg-brand-blue/20 text-info',
  green: 'bg-success/15 text-success',
  teal: 'bg-brand-teal/15 text-link',
  neutral: 'bg-surface-3 text-text-2',
  warning: 'bg-warning/15 text-warning',
  danger: 'bg-danger/15 text-danger',
};

export function Tag({ tone = 'neutral', onRemove, size = 'md', className, children, ...rest }: TagProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-chip font-medium',
        size === 'sm' ? 'h-6 px-2 text-xs' : 'h-8 px-3 text-[13px]',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${typeof children === 'string' ? children : 'tag'}`}
          className="-mr-1 rounded-sm p-0.5 opacity-70 hover:opacity-100"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

/** Status badge: ACTIVE, CURRENT, Pro, Idle… */
export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: TagTone;
  dot?: boolean;
  pulse?: boolean;
  uppercase?: boolean;
}

export function Badge({ tone = 'neutral', dot, pulse, uppercase, className, children, ...rest }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-chip px-2 py-0.5 text-[11px] font-semibold tracking-wide',
        uppercase && 'uppercase',
        tones[tone],
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn('size-1.5 rounded-full bg-current', pulse && 'animate-pulse-dot')} aria-hidden />}
      {children}
    </span>
  );
}

/** Mono short-code chip with teal tint (e.g. `nkn6bA`). */
export function CodeChip({ className, children, ...rest }: HTMLAttributes<HTMLSpanElement>) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-chip border border-brand-teal/25 bg-brand-teal/10 px-2.5 py-1 font-mono text-[13px] text-link',
        className,
      )}
      {...rest}
    >
      {children}
    </span>
  );
}

/** Keyboard hint like ⌘K. */
export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="rounded-[4px] border border-border bg-surface-3 px-1.5 py-0.5 font-mono text-[11px] text-muted">{children}</kbd>
  );
}
